import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { Check, LineChart, PencilRuler, Rocket, SlidersHorizontal } from 'lucide-react'
import {
  AskAIPill,
  AskAISheet,
  Button,
  ChatHeader,
  Navbar,
  StrategyModeToggle,
  StrategyNavTabs,
  Workbench,
} from '@/components'
import { botApi } from './api'
import { backtestCoachStore } from './backtestCoach'
import { chartPush, useChartPush } from './chartPush'
import BacktestCoach from './components/BacktestCoach'
import BotChat from './components/BotChat'
import ChartFlyChip from './components/ChartFlyChip'
import BotBacktest from './components/BotBacktest'
import BotOptimizer from './components/BotOptimizer'
import BotLive from './BotLive'
import { useSSE } from '../contexts/SSEContext'
import { SSE_EVENTS, MESSAGE_STATUS } from '../contexts/SSEEvents'
import ResizablePanel from '../components-legacy/ResizablePanel'

const PHONE_QUERY = '(max-width: 639px)'

const STRATEGY_NAV_TABS = [
  { id: 'workbench', label: 'Create', icon: PencilRuler },
  { id: 'backtest', label: 'Backtest', icon: LineChart },
  { id: 'optimizer', label: 'Optimise', icon: SlidersHorizontal },
]

const UPGRADE_MSG_ID = 'credits-exhausted'
const CREDITS_EXHAUSTED_MESSAGE =
  "You've used all of your credits. Visit billing to manage your subscription and credit usage."

export default function BotDetail() {
  const { bid } = useParams()
  const navigate = useNavigate()
  const outletContext = useOutletContext() || {}
  const { isMobile = false, setDrawerOpen } = outletContext

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  // No ?tab= param → the Create (workbench) tab is the landing tab.
  const topTab =
    tabParam === 'optimizer'
      ? 'optimizer'
      : tabParam === 'backtest'
        ? 'backtest'
        : 'workbench'
  const mode = searchParams.get('mode') === 'published' ? 'published' : 'builder'
  const chatParam = searchParams.get('chat')
  const chatHidden = chatParam === '0'
  const mobileChatMode =
    chatParam === 'sheet' || chatParam === 'expanded' ? chatParam : 'pill'

  const updateSearchParam = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null || value === undefined) next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const [bot, setBot] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Chat state. The draft text lives entirely inside the uncontrolled
  // <ChatInput> so typing never re-renders BotDetail or the heavy
  // main-column subtree (charts, etc.). The send handler receives the
  // text via its argument when the user submits.
  const [messages, setMessages] = useState([])
  const [isChatLoading, setIsChatLoading] = useState(false)

  // Chart context pushed from the Workbench, staged on the chat composer.
  const push = useChartPush(bid)

  const handleUpgradeClick = useCallback(() => navigate('/billing'), [navigate])

  const showCreditsExhaustedMessage = useCallback(() => {
    setMessages((prev) => [
      ...prev.filter((m) => m.id !== UPGRADE_MSG_ID),
      {
        id: UPGRADE_MSG_ID,
        role: 'upgrade',
        content: CREDITS_EXHAUSTED_MESSAGE,
        timestamp: new Date(),
      },
    ])
  }, [])

  // Code state
  const [currentCode, setCurrentCode] = useState(null)
  const [hasCode, setHasCode] = useState(false)

  // Lower panel tab state (Results / Equity / Trades / Logs / Code / Input / Entries / PnL / Results old)
  const [lowerTab, setLowerTab] = useState('results')

  // Refresh triggers for backtest and optimizer
  const [backtestRefreshTrigger, setBacktestRefreshTrigger] = useState(0)
  const [optimizerRefreshTrigger, setOptimizerRefreshTrigger] = useState(0)
  const queuedBacktestRefreshKeysRef = useRef(new Set())

  // Publish state
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishRefreshTrigger, setPublishRefreshTrigger] = useState(0)
  const [deployPulseId, setDeployPulseId] = useState(null)
  const deployPulseTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (deployPulseTimerRef.current) {
        clearTimeout(deployPulseTimerRef.current)
        deployPulseTimerRef.current = null
      }
    }
  }, [])

  // Phone detection (< 640) for icon-only deploy CTA
  const [isPhone, setIsPhone] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(PHONE_QUERY).matches : false,
  )

  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY)
    const handler = (e) => setIsPhone(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Cross-breakpoint sanitization: ?chat=sheet|expanded is mobile-only,
  // ?chat=0 is desktop-only. Drop stale values when isMobile flips so the URL
  // never carries a state that doesn't match the current breakpoint.
  useEffect(() => {
    if (isMobile && chatParam === '0') {
      updateSearchParam('chat', null)
    } else if (!isMobile && (chatParam === 'sheet' || chatParam === 'expanded')) {
      updateSearchParam('chat', null)
    }
  }, [isMobile, chatParam, updateSearchParam])

  useEffect(() => {

    if (!bid) {
      setError('Strategy ID is missing')
      setIsLoading(false)
      return
    }

    // Reset all state when bot changes
    setMessages([])
    setIsChatLoading(false)
    setCurrentCode(null)
    setHasCode(false)
    queuedBacktestRefreshKeysRef.current.clear()
    backtestCoachStore.reset()
    chartPush.ensure(bid)

    loadBot()
    initializeChat()
    loadCurrentCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid])

  // Subscribe to code generation events
  useSSE(
    useCallback((msg) => msg.event === SSE_EVENTS.BOT_CODE_GENERATED && msg.botId === bid, [bid]),
    useCallback(
      (message) => {
        console.log('Code generated event received:', message)
        setHasCode(true)
        if (bid) {
          botApi
            .getCurrentCode(bid)
            .then((code) => {
              if (code) {
                setCurrentCode(code)
                setHasCode(true)
              }
            })
            .catch((err) => {
              console.error('Error loading code:', err)
            })
        }
        // No tab switch: the chat message announces the generated code; the
        // Code sub-tab reads currentCode whenever the user opens it.
      },
      [bid],
    ),
  )

  // Subscribe to optimizer completion events
  useSSE(
    useCallback((msg) => msg.event === SSE_EVENTS.BOT_OPTIMIZER_COMPLETED && msg.botId === bid, [bid]),
    useCallback(
      (message) => {
        console.log('Optimizer completed event received:', message)
        setOptimizerRefreshTrigger((prev) => prev + 1)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [bid],
    ),
  )

  // Subscribe to backtest completion events. No tab switch — the chat message
  // announces completion; refresh so an already-open Backtest tab shows the
  // final results, and a later manual visit refetches on mount anyway.
  useSSE(
    useCallback((msg) => msg.event === SSE_EVENTS.BOT_BACKTEST_COMPLETED && msg.botId === bid, [bid]),
    useCallback((message) => {
      console.log('Backtest completed event received:', message)
      setBacktestRefreshTrigger((prev) => prev + 1)
    }, []),
  )

  const loadBot = async () => {
    if (!bid) return

    setIsLoading(true)
    setError(null)

    try {
      const data = await botApi.get(bid)
      setBot(data)
    } catch (err) {
      console.error('Error loading bot:', err)
      setError('Failed to load strategy details')
    } finally {
      setIsLoading(false)
    }
  }

  const initializeChat = async () => {
    if (!bid) return

    try {
      const data = await botApi.getMessages(bid)

      const transformMessage = (msg) => {
        // Handle tool messages (new format with role: 'tool')
        if (msg.role === 'tool') {
          try {
            const parsed = JSON.parse(msg.content)

            // console.log('Loading tool message from database:', {
            //   msgId: msg.msgId,
            //   status: msg.status,
            //   toolName: parsed.name,
            //   toolStatus: parsed.status,
            // })

            return {
              id: msg.msgId.toString(),
              role: 'assistant',
              content: '',
              timestamp: new Date(parsed.startedAt || new Date()),
              status: msg.status,
              messageType: 'tool',
              toolData: {
                toolName: parsed.name,
                toolInput: parsed.arguments,
                callId: parsed.call_id,
                startedAt: parsed.startedAt,
                completedAt: parsed.completedAt,
                status: parsed.status,
                componentType: parsed.componentType,
                componentData: parsed.componentData,
                responses: parsed.responses,
                dismissed: parsed.dismissed,
                output: parsed.output,
                toolStatus: parsed.toolStatus,
                btid: parsed.btid,
                cid: parsed.cid,
                optid: parsed.optid,
                version: parsed.version,
                codeVersion: parsed.codeVersion,
                codeId: parsed.codeId,
                config: parsed.config,
                error: parsed.error,
              },
            }
          } catch (err) {
            console.error('Error parsing tool message:', err)
            return {
              id: msg.msgId.toString(),
              role: 'assistant',
              content: '[Error loading tool message]',
              timestamp: new Date(),
              status: msg.status,
              messageType: 'content',
            }
          }
        }

        if (msg.role === 'assistant') {
          return {
            id: msg.msgId.toString(),
            role: 'assistant',
            content: msg.content,
            timestamp: new Date(),
            status: msg.status,
            messageType: 'content',
          }
        }

        return {
          id: msg.msgId.toString(),
          role: msg.role,
          content: msg.content,
          timestamp: new Date(),
          status: msg.status,
        }
      }

      // Rows with role 'chart_context' carry the chart snapshot sent alongside
      // the following user message. Their content is ALREADY a parsed object
      // ({ label, text }) — hold it, skip the row, and surface it as a chip
      // attachment on the next user message.
      const transformedMessages = []
      let pendingChartContext = null
      for (const msg of data.messages) {
        if (msg.role === 'chart_context') {
          if (typeof msg.content === 'object' && msg.content?.label) {
            pendingChartContext = msg.content
          }
          continue
        }

        const transformed = transformMessage(msg)
        if (msg.role === 'user' && pendingChartContext) {
          transformed.attachments = [{ name: pendingChartContext.label, kind: 'chart' }]
          pendingChartContext = null
        }
        transformedMessages.push(transformed)
      }

      // Merge with any messages already added via SSE to avoid the race where
      // SSE events arrive before this HTTP response and get overwritten.
      setMessages((prev) => {
        if (prev.length === 0) return transformedMessages

        const httpIds = new Set(transformedMessages.map((m) => m.id))
        const sseOnly = prev.filter((m) => !httpIds.has(m.id))
        return [...transformedMessages, ...sseOnly]
      })

      const hasInProgress = transformedMessages.some(
        (m) => m.status === MESSAGE_STATUS.IN_PROGRESS,
      )
      if (hasInProgress) {
        setIsChatLoading(true)
      }
    } catch (err) {
      console.error('Error loading messages:', err)
    }
  }

  const loadCurrentCode = async () => {
    if (!bid) return

    try {
      const code = await botApi.getCurrentCode(bid)
      if (code) {
        setCurrentCode(code)
        setHasCode(true)
      }
    } catch (err) {
      console.error('Error loading code:', err)
    }
  }

  const handlePublishesEmpty = () => {
    setCurrentCode((prev) => (prev ? { ...prev, isPublished: false } : prev))
  }

  const handlePublishBot = async () => {
    if (!bid) return
    setIsPublishing(true)
    try {
      await botApi.publishBot(bid)
      setCurrentCode((prev) => (prev ? { ...prev, isPublished: true } : prev))
      setPublishRefreshTrigger((prev) => prev + 1)
      setDeployPulseId((prev) => (prev ?? 0) + 1)
      if (deployPulseTimerRef.current) {
        clearTimeout(deployPulseTimerRef.current)
      }
      deployPulseTimerRef.current = setTimeout(() => {
        setDeployPulseId(null)
        deployPulseTimerRef.current = null
      }, 1800)
    } catch (err) {
      const errorCode =
        err && typeof err === 'object' && 'errorCode' in err
          ? String(err.errorCode)
          : ''
      const message =
        errorCode === 'CODE_ALREADY_PUBLISHED'
          ? 'This strategy code has already been deployed.'
          : errorCode === 'CODE_NOT_FOUND'
            ? 'No code found for this strategy.'
            : 'Failed to deploy the strategy. Please try again.'
      alert(message)
    } finally {
      setIsPublishing(false)
    }
  }

  const handleSSEMessage = useCallback(
    (message) => {
      console.log(
        'SSE message received:',
        message.event,
        'botId:',
        message.botId,
        'msgId:',
        message.msgId,
        'status:',
        message.status,
      )

      if (message.event === SSE_EVENTS.BOT_MESSAGE_USER) {
        const userMessage = {
          id: message.msgId.toString(),
          role: 'user',
          content: message.content,
          timestamp: new Date(),
          status: MESSAGE_STATUS.PROCESSED,
          ...(message.attachmentLabel
            ? { attachments: [{ name: message.attachmentLabel, kind: 'chart' }] }
            : {}),
        }
        setMessages((prev) => [...prev, userMessage])
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_STARTED) {
        setIsChatLoading(true)
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.msgId.toString())) {
            return prev
          }
          return [
            ...prev,
            {
              id: message.msgId.toString(),
              role: 'assistant',
              content: '',
              timestamp: new Date(),
              status: MESSAGE_STATUS.IN_PROGRESS,
            },
          ]
        })
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_CHUNK) {
        setIsChatLoading(true)

        setMessages((prev) => {
          const messageId = message.msgId.toString()
          const existingIndex = prev.findIndex((m) => m.id === messageId)

          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              content: message.accumulated,
              streaming: true,
              status: MESSAGE_STATUS.IN_PROGRESS,
            }
            return updated
          } else {
            return [
              ...prev,
              {
                id: messageId,
                role: 'assistant',
                content: message.accumulated,
                timestamp: new Date(),
                streaming: true,
                status: MESSAGE_STATUS.IN_PROGRESS,
              },
            ]
          }
        })
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_DONE) {
        console.log('Received bot-message-done, clearing loading state', {
          msgId: message.msgId,
          status: message.status,
          messageType: message.messageType,
        })
        setIsChatLoading(false)

        setMessages((prev) => {
          return prev.map((msg) => {
            if (msg.id === message.msgId.toString()) {
              if (message.messageType === 'tool') {
                return {
                  ...msg,
                  streaming: false,
                  status: MESSAGE_STATUS.PROCESSED,
                  messageType: 'tool',
                  toolData: msg.toolData
                    ? {
                        ...msg.toolData,
                        status: 'completed',
                        completedAt: message.completedAt || msg.toolData.completedAt,
                        ...(message.toolStatus && { toolStatus: message.toolStatus }),
                        ...(message.dismissed && { dismissed: true }),
                        ...(message.responses && { responses: message.responses }),
                        ...(message.btid && { btid: message.btid }),
                        ...(message.cid && { cid: message.cid }),
                        ...(message.optid && { optid: message.optid }),
                        ...(message.version != null && { version: message.version }),
                        ...(message.codeVersion != null && { codeVersion: message.codeVersion }),
                        ...(message.config && { config: message.config }),
                        ...(message.error && { error: message.error }),
                      }
                    : undefined,
                }
              }
              return {
                ...msg,
                content: message.content,
                streaming: false,
                status: MESSAGE_STATUS.PROCESSED,
                messageType: 'content',
              }
            }
            return msg
          })
        })

        if (
          message.toolStatus === 'BACKTEST_ERROR' ||
          message.toolStatus === 'BACKTEST_CANCELED'
        ) {
          setBacktestRefreshTrigger((prev) => prev + 1)
        }

        // First-ever completed backtest starts the onboarding coach, anchored
        // to this message's results badge (no-op once seen or while running).
        if (message.toolStatus === 'BACKTEST_COMPLETED' && message.btid) {
          backtestCoachStore.start(message.msgId.toString())
        }
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_DELETED) {
        console.log('Received bot-message-deleted', { msgId: message.msgId })
        setIsChatLoading(false)
        setMessages((prev) => prev.filter((msg) => msg.id !== message.msgId.toString()))
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_FAILED) {
        console.log('Received bot-message-failed', {
          msgId: message.msgId,
          error: message.error,
        })
        setIsChatLoading(false)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.msgId.toString()
              ? { ...msg, streaming: false, status: MESSAGE_STATUS.FAILED, error: message.error }
              : msg,
          ),
        )
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_CANCELED) {
        console.log('Received bot-message-canceled', { msgId: message.msgId })
        setIsChatLoading(false)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.msgId.toString()
              ? { ...msg, streaming: false, status: MESSAGE_STATUS.CANCELED }
              : msg,
          ),
        )
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_TYPE_CHANGED) {
        console.log('Received bot-message-type-changed', {
          msgId: message.msgId,
          messageType: message.messageType,
          toolName: message.toolName,
        })

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.msgId.toString()
              ? {
                  ...msg,
                  messageType: 'tool',
                  streaming: false,
                  toolData: {
                    toolName: message.toolName,
                    toolInput: message.toolInput || {},
                    startedAt: message.startedAt,
                    status: 'running',
                    componentType: message.componentType,
                    componentData: message.componentData,
                    toolStatus: message.toolStatus,
                  },
                }
              : msg,
          ),
        )
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_TOOL_STARTED) {
        console.log('Received bot-message-tool-started', {
          msgId: message.msgId,
          toolName: message.toolName,
          callId: message.callId,
          toolInput: message.toolInput,
        })

        setIsChatLoading(true)
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.msgId.toString())) {
            return prev
          }
          return [
            ...prev,
            {
              id: message.msgId.toString(),
              role: 'assistant',
              content: '',
              timestamp: new Date(message.startedAt || new Date()),
              status: MESSAGE_STATUS.IN_PROGRESS,
              messageType: 'tool',
              toolData: {
                toolName: message.toolName,
                toolInput: message.toolInput || {},
                callId: message.callId,
                startedAt: message.startedAt || new Date().toISOString(),
                status: 'running',
              },
            },
          ]
        })
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_TOOL_DONE) {
        console.log('Received bot-message-tool-done', {
          msgId: message.msgId,
          toolName: message.toolName,
        })

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === message.msgId.toString() && msg.toolData) {
              return {
                ...msg,
                status: MESSAGE_STATUS.PROCESSED,
                toolData: {
                  ...msg.toolData,
                  status: 'completed',
                  ...(message.cid && { cid: message.cid }),
                  ...(message.version != null && { version: message.version }),
                },
              }
            }
            return msg
          }),
        )
      }

      if (message.event === SSE_EVENTS.BOT_BACKTEST_STATUS) {
        console.log('Received bot-backtest-status', {
          msgId: message.msgId,
          toolStatus: message.toolStatus,
          backtestStatus: message.backtestStatus,
        })

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === message.msgId?.toString()) {
              return {
                ...msg,
                toolData: {
                  ...msg.toolData,
                  toolStatus: message.toolStatus,
                  config: message.config || msg.toolData?.config,
                  btid: message.btid || msg.toolData?.btid,
                  cid: message.cid || msg.toolData?.cid,
                  version: message.version ?? msg.toolData?.version,
                  codeVersion: message.codeVersion ?? msg.toolData?.codeVersion,
                  error: message.error || msg.toolData?.error,
                },
              }
            }
            return msg
          }),
        )

        if (message.toolStatus === 'BACKTEST_QUEUED') {
          setBacktestRefreshTrigger((prev) => prev + 1)
        }

        if (message.toolStatus === 'BACKTEST_RUNNING') {
          const refreshKey =
            message.msgId != null
              ? `msg:${message.msgId}`
              : message.btid
                ? `btid:${message.btid}`
                : null

          if (refreshKey && !queuedBacktestRefreshKeysRef.current.has(refreshKey)) {
            queuedBacktestRefreshKeysRef.current.add(refreshKey)
            setBacktestRefreshTrigger((prev) => prev + 1)
          }
        }
      }

      if (message.event === SSE_EVENTS.BOT_OPTIMIZER_STATUS) {
        console.log('Received bot-optimizer-status', {
          msgId: message.msgId,
          toolStatus: message.toolStatus,
          optimizerStatus: message.optimizerStatus,
        })

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === message.msgId?.toString()) {
              return {
                ...msg,
                toolData: {
                  ...msg.toolData,
                  toolStatus: message.toolStatus,
                  config: message.config || msg.toolData?.config,
                  optid: message.optid || msg.toolData?.optid,
                  version: message.version ?? msg.toolData?.version,
                  codeVersion: message.codeVersion ?? msg.toolData?.codeVersion,
                  error: message.error || msg.toolData?.error,
                },
              }
            }
            return msg
          }),
        )

        if (
          message.toolStatus === 'OPTIMIZER_QUEUED' ||
          message.toolStatus === 'OPTIMIZER_RUNNING'
        ) {
          setOptimizerRefreshTrigger((prev) => prev + 1)
        }
      }
    },
    [],
  )

  // Subscribe to SSE messages for this bot
  useSSE(
    useCallback((msg) => msg.botId === bid, [bid]),
    handleSSEMessage,
  )

  // Subscribe to credits-exhausted SSE event (fired from fire-and-forget startFirstMessage)
  useSSE(
    useCallback(
      (msg) => msg.event === SSE_EVENTS.BOT_CREDITS_EXHAUSTED && msg.botId === bid,
      [bid],
    ),
    useCallback(() => {
      setIsChatLoading(false)
      showCreditsExhaustedMessage()
    }, [showCreditsExhaustedMessage]),
  )

  const handleSendMessage = async (prompt) => {
    const text = (prompt ?? '').trim()
    if (!text || isChatLoading || !bid) return

    setIsChatLoading(true)

    // Chart context staged on the composer rides along with this message.
    // Commit only AFTER the send succeeds — a failed send keeps the chip.
    const staged = chartPush.get(bid).staged

    try {
      await botApi.sendMessage(
        bid,
        text,
        staged ? { label: staged.label, text: staged.text } : undefined,
      )
      if (staged) chartPush.commit(bid)
    } catch (err) {
      setIsChatLoading(false)
      if (err?.errorCode === 'INSUFFICIENT_CREDITS') {
        showCreditsExhaustedMessage()
      } else {
        console.error('Error sending message:', err)
      }
    }
  }

  // Badge actions on a completed backtest's results card.
  const handleRunOptimizer = () => handleSendMessage('run the parameter optimizer')

  const handleViewReport = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', 'backtest')
        // On mobile the chat sheet covers the tabs — close it too.
        if (isMobile) next.delete('chat')
        return next
      },
      { replace: true },
    )
  }, [setSearchParams, isMobile])

  const handleCancelMessage = async () => {
    if (!bid) return

    console.log('Canceling message for bot:', bid)

    try {
      await botApi.cancelMessage(bid)
    } catch (err) {
      console.error('Error canceling message:', err)
    }
  }

  const handleRetryMessage = async () => {
    if (!bid) return

    console.log('Retrying message for bot:', bid)
    setIsChatLoading(true)

    try {
      await botApi.retryMessage(bid)
    } catch (err) {
      setIsChatLoading(false)
      if (err?.errorCode === 'INSUFFICIENT_CREDITS') {
        showCreditsExhaustedMessage()
      } else {
        console.error('Error retrying message:', err)
      }
    }
  }

  const handleComponentSubmit = async (msgId, answers) => {
    if (!bid) return

    console.log('Component submitted:', msgId, answers)

    const msgIdNum = parseInt(msgId)

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === msgId && msg.toolData) {
          return {
            ...msg,
            status: MESSAGE_STATUS.PROCESSED,
            toolData: {
              ...msg.toolData,
              responses: answers,
              status: 'completed',
            },
          }
        }
        return msg
      }),
    )

    setIsChatLoading(true)
    try {
      await botApi.submitFormResponse(bid, msgIdNum, answers)
    } catch (err) {
      setIsChatLoading(false)
      if (err?.errorCode === 'INSUFFICIENT_CREDITS') {
        showCreditsExhaustedMessage()
      } else {
        console.error('Error sending component response:', err)
        // Reconcile the optimistic update: another tab may have already
        // submitted or dismissed this form (MESSAGE_ALREADY_PROCESSED).
        initializeChat()
      }
    }
  }

  const handleComponentDismiss = async (msgId) => {
    if (!bid) return

    console.log('Component dismissed:', msgId)

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === msgId && msg.toolData) {
          return {
            ...msg,
            status: MESSAGE_STATUS.PROCESSED,
            toolData: {
              ...msg.toolData,
              status: 'completed',
              dismissed: true,
              responses: {},
            },
          }
        }
        return msg
      }),
    )

    // Dismissing triggers no AI continuation — nothing is running anymore.
    setIsChatLoading(false)

    try {
      await botApi.dismissForm(bid, parseInt(msgId))
    } catch (err) {
      console.error('Error dismissing questions:', err)
      // Reconcile the optimistic update with the authoritative state.
      initializeChat()
    }
  }

  const handleMenuClick = useCallback(() => {
    if (typeof setDrawerOpen === 'function') setDrawerOpen((open) => !open)
  }, [setDrawerOpen])

  const showChat = !chatHidden && !isMobile

  const handleShowChat = useCallback(() => {
    if (isMobile) {
      updateSearchParam('chat', 'sheet')
    } else {
      updateSearchParam('chat', null)
    }
  }, [isMobile, updateSearchParam])

  const handleCollapseChat = useCallback(() => {
    if (isMobile) {
      updateSearchParam('chat', null)
    } else {
      updateSearchParam('chat', '0')
    }
  }, [isMobile, updateSearchParam])

  // "Add to AI chat" while the chat is hidden (mobile: pill; desktop:
  // collapsed) — open it so the user sees the staged chip on the composer.
  const chatVisible = isMobile ? mobileChatMode !== 'pill' : !chatHidden
  useEffect(() => {
    if (chatVisible) return undefined
    return chartPush.onStage(handleShowChat)
  }, [chatVisible, handleShowChat])

  const deployVersionLabel = currentCode?.version != null ? `v${currentCode.version}` : null

  const deployCtaSlot = (() => {
    if (!hasCode || !currentCode) return null
    if (currentCode.isPublished) {
      return (
        <span
          aria-label={
            deployVersionLabel ? `Deployed ${deployVersionLabel}` : 'Deployed'
          }
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-muted text-caption text-muted-foreground select-none"
        >
          <Check size={11} strokeWidth={2.25} className="text-foreground" />
          {!isPhone && 'Deployed'}
          {deployVersionLabel && (
            <span className="font-mono tabular-nums opacity-70">
              {deployVersionLabel}
            </span>
          )}
        </span>
      )
    }
    return (
      <Button
        type="button"
        size="sm"
        onClick={isPublishing ? undefined : handlePublishBot}
        disabled={isPublishing}
        className="h-7 min-h-7 gap-1.5 text-caption px-3"
        title={
          isPhone
            ? deployVersionLabel
              ? `Deploy ${deployVersionLabel}`
              : 'Deploy'
            : undefined
        }
      >
        <Rocket size={10} strokeWidth={2} />
        {!isPhone && 'Deploy'}
        {deployVersionLabel && (
          <span className="font-mono tabular-nums opacity-70">
            {deployVersionLabel}
          </span>
        )}
      </Button>
    )
  })()

  if (isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-body text-muted-foreground">
        Loading...
      </div>
    )
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-body text-danger">
        {error}
      </div>
    )
  }

  if (!bot) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-body text-muted-foreground">
        Strategy not found
      </div>
    )
  }

  const chatProps = {
    messages,
    onSendMessage: handleSendMessage,
    onComponentSubmit: handleComponentSubmit,
    onComponentDismiss: handleComponentDismiss,
    onCancelMessage: handleCancelMessage,
    onRetryMessage: handleRetryMessage,
    onUpgradeClick: handleUpgradeClick,
    onRunOptimizer: handleRunOptimizer,
    onViewReport: handleViewReport,
    isLoading: isChatLoading,
    hasCode,
    // The backtest quick action shows on every builder tab
    // (published mode has no tab nav at all).
    showBacktestAction: mode === 'builder',
    stagedAttachment: push.staged
      ? { label: push.staged.label, text: push.staged.text }
      : null,
    onRemoveStagedAttachment: () => chartPush.clearStaged(bid),
    onUpdateStagedAttachment: (text) => chartPush.updateStagedText(bid, text),
  }

  const renderMainContent = () => {
    if (mode === 'published') {
      return (
        <BotLive
          refreshTrigger={publishRefreshTrigger}
          onEmpty={handlePublishesEmpty}
        />
      )
    }
    if (topTab === 'workbench') {
      // Keyed by bid: navigating to another strategy must remount the workbench
      // so its persisted chart state restores cleanly and never crosses bots.
      return <Workbench key={bot.bid} bid={bot.bid} />
    }
    if (topTab === 'optimizer') {
      return <BotOptimizer bid={bot.bid} refreshTrigger={optimizerRefreshTrigger} />
    }
    return (
      <BotBacktest
        bid={bot.bid}
        refreshTrigger={backtestRefreshTrigger}
        currentCode={currentCode}
        hasCode={hasCode}
        activeBottomTab={lowerTab}
        onActiveBottomTabChange={setLowerTab}
      />
    )
  }

  const tabsSlot =
    mode === 'builder' ? (
      <StrategyNavTabs
        tabs={STRATEGY_NAV_TABS}
        activeId={topTab}
        onTabChange={(id) => updateSearchParam('tab', id === 'workbench' ? null : id)}
        isMobile={isMobile}
      />
    ) : null

  const modeToggleSlot = (
    <StrategyModeToggle
      mode={mode}
      onModeChange={(next) => updateSearchParam('mode', next === 'builder' ? null : next)}
      deployPulseId={deployPulseId}
    />
  )

  const renderMainColumn = ({ showChatButton } = {}) => (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-card">
      <Navbar
        isMobile={isMobile}
        isPhone={isPhone}
        onMenuClick={isMobile ? handleMenuClick : undefined}
        showBorder
        tabsSlot={tabsSlot}
        modeToggleSlot={modeToggleSlot}
        showChatButton={!!showChatButton}
        onShowChat={handleShowChat}
      />
      <main className="flex-1 min-h-0 relative overflow-hidden">
        {renderMainContent()}
      </main>
    </div>
  )

  const renderChatColumn = ({
    onCollapse,
    isMobileLayout,
    hideHeader = false,
    inputTrailingSlot,
  }) => (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-background">
      {!hideHeader && (
        <ChatHeader
          title="Chat"
          onCollapse={onCollapse}
          deployCtaSlot={deployCtaSlot}
        />
      )}
      <div className="flex-1 min-h-0 relative">
        <BotChat
          {...chatProps}
          hideHeader
          isMobileLayout={isMobileLayout}
          inputTrailingSlot={inputTrailingSlot}
        />
      </div>
    </div>
  )

  return (
    <div className="absolute inset-0 overflow-hidden bg-card">
      <ChartFlyChip />
      <BacktestCoach
        isMobile={isMobile}
        onCloseMobileChat={() => updateSearchParam('chat', null)}
      />
      {isMobile ? (
        renderMainColumn()
      ) : showChat ? (
        <ResizablePanel
          defaultLeftWidth={60}
          minLeftWidth={30}
          maxLeftWidth={80}
          storageKey={`bot-detail-panel-${bid}`}
        >
          {renderMainColumn()}
          {renderChatColumn({ onCollapse: handleCollapseChat, isMobileLayout: false })}
        </ResizablePanel>
      ) : (
        renderMainColumn({ showChatButton: true })
      )}

      {isMobile && (
        <>
          <AskAIPill
            onOpen={() => updateSearchParam('chat', 'sheet')}
            hidden={mobileChatMode !== 'pill'}
            badge={
              push.staged ? (
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background" />
              ) : undefined
            }
          />
          <AskAISheet
            mode={mobileChatMode === 'pill' ? 'hidden' : mobileChatMode}
            onExpand={() => updateSearchParam('chat', 'expanded')}
            onCollapse={() => updateSearchParam('chat', 'sheet')}
            onClose={() => updateSearchParam('chat', null)}
            chatPanelSlot={
              <div className="relative w-full h-full">
                {renderChatColumn({
                  onCollapse: () => updateSearchParam('chat', null),
                  isMobileLayout: true,
                  hideHeader: true,
                  inputTrailingSlot: deployCtaSlot,
                })}
              </div>
            }
          />
        </>
      )}
    </div>
  )
}
